import { Component, computed, inject, input, signal, type TemplateRef } from "@angular/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faArrowLeftLong, faArrowRightLong, faChartSimple } from "@fortawesome/free-solid-svg-icons";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { EMPTY, filter, switchMap, tap } from "rxjs";

import { TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { CalculationDataType } from "../../../constants/enums";
import type {
  AggregationPerfModelType,
  AggregationWeights,
  AverageEntityPerformanceModelType,
  EntityPerformanceModelType,
  EntityPeriodFilterModel,
  GetCalculationValuesArgs,
  SelectedPeriodModelType,
} from "../../../models";
import { AggregationPerfService, CalculationEngineApiService } from "../../../services";
import { DoughnutComponent } from "../../charts";
import { AggregationPerfCardComponent } from "../aggregation-perf-card/aggregation-perf-card";
import { EntityComparisonModalComponent } from "../entity-comparison-modal/entity-comparison-modal";
import { ItemSliderComponent } from "../item-slider/item-slider";

@Component({
  selector: "adaa-aggregated-performance",
  templateUrl: "./aggregated-performance.html",
  styleUrl: "./aggregated-performance.scss",
  imports: [
    EntityComparisonModalComponent,
    FontAwesomeModule,
    DoughnutComponent,
    TranslateTokenPipe,
    AggregationPerfCardComponent,
    ItemSliderComponent,
    TranslatePipe,
  ],
})
export class AggregatedPerformanceComponent {
  readonly modalService = inject(NgbModal);
  private readonly _calculationEngineApiService = inject(CalculationEngineApiService);
  private readonly _aggregationPerfService = inject(AggregationPerfService);

  isPmo = input.required<boolean>();
  selectedPeriod = input<SelectedPeriodModelType>();
  aggregatedWeights = input<AggregationWeights>();

  entityPeriodFilter = signal<EntityPeriodFilterModel | undefined>(undefined);
  selectedAggregationType = signal<AggregationPerfModelType | undefined>(undefined);
  entitiesPerformance = signal<EntityPerformanceModelType[]>([]);
  averagePerformance = signal<AverageEntityPerformanceModelType | undefined>(undefined);

  showEntityTypeAndSize = computed(() => {
    if (!this.isPmo()) return false;
    if (this.entityPeriodFilter()) return this.entityPeriodFilter()?.periodType === "year";
    return this.selectedPeriod()?.type === "year";
  });
  aggregationPerf = computed(() => this._aggregationPerfService.aggregationPerf());
  overallAggregationPerf = computed(() => this._aggregationPerfService.overallAggregationPerf());
  showOverall = computed(() => this.overallAggregationPerf()?.enabled ?? false);

  public get icon() {
    return {
      leftArrow: faArrowLeftLong,
      rightArrow: faArrowRightLong,
      graph: faChartSimple,
    };
  }

  public getColor(code: number) {
    return Constants.COLOR_CODES[code];
  }

  public get chartTitle() {
    return AdaaHelper.getItemValueByToken(this.selectedAggregationType(), "label");
  }

  public onFilterChange(event: EntityPeriodFilterModel) {
    this.entityPeriodFilter.update(() => event);
    this._fetchPerformanceData();
  }

  public openPerformanceGraph(content: TemplateRef<unknown>) {
    // Always reset the overridden period when opening the modal
    this.entityPeriodFilter.update(() => undefined);

    this.modalService.open(content, {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "xl",
      centered: true,
      fullscreen: false,
      ariaLabelledBy: this.chartTitle,
    });

    this._fetchPerformanceData();
  }

  private _fetchPerformanceData() {
    let periodId = this.selectedPeriod()?.type === "quarter" ? this.selectedPeriod()?.period?.id : undefined;
    let year = this.selectedPeriod()?.type === "year" ? this.selectedPeriod()?.period?.year : undefined;

    if (this.entityPeriodFilter()?.period) {
      periodId = this.entityPeriodFilter()?.periodType === "quarter" ? this.entityPeriodFilter()?.period : undefined;
      year = this.entityPeriodFilter()?.periodType === "year" ? this.entityPeriodFilter()?.period : undefined;
    }

    const params = {
      type: CalculationDataType.ENTITY,
      governmentAggregationType: AdaaHelper.isPMOEntity()
        ? (this.selectedAggregationType()?.aggregationType as number)
        : undefined,
      entityAggregationType: !AdaaHelper.isPMOEntity()
        ? (this.selectedAggregationType()?.aggregationType as number)
        : undefined,
      year: year,
      periodId: periodId,
      entitySize: this.entityPeriodFilter()?.entitySize,
      entityType: this.entityPeriodFilter()?.entityType,
    };

    this._calculationEngineApiService
      .getCalculationData(params)
      .pipe(
        tap((res) => {
          if (res.inError) return;
          this.entitiesPerformance.update(() => res.responseData as EntityPerformanceModelType[]);
        }),
        switchMap(() => this._getAveragePerformance(params)),
        filter((res) => AdaaHelper.isDefined(res)),
        tap((res) => {
          if (res.inError) return;
          const score = (res.responseData as AverageEntityPerformanceModelType) || undefined;
          this.averagePerformance.update(() => score);
        })
      )
      .subscribe();
  }

  private _getAveragePerformance(params: GetCalculationValuesArgs) {
    if (!this.isPmo()) return EMPTY;
    return this._calculationEngineApiService.getCalculationData({
      ...params,
      type: CalculationDataType.GOVERNMENT,
    });
  }

  public hasWeightsProblem(aggType: number, warningTranslationKey: string | null = null) {
    const aggregatedWeights = this.aggregatedWeights();
    if (!aggregatedWeights) return { value: false };
    for (const key of Object.keys(aggregatedWeights)) {
      if (this.selectedPeriod()?.type === "quarter") {
        continue;
      }

      if (+key !== +aggType) {
        continue;
      }

      const agg = this.aggregatedWeights()![+key];

      if (!AdaaHelper.isDefinedAndNotEmpty(agg.perYear[this.selectedPeriod()!.period.year])) {
        return { value: false };
      }

      return {
        value: agg.perYear[this.selectedPeriod()!.period.year].weightIssue,
        message: "kpi_annual_weights.tooltips.kpi_annual_weights_issue_homepage",
      };
    }

    if (AdaaHelper.isDefinedAndNotEmpty(warningTranslationKey)) {
      return { value: true, message: warningTranslationKey };
    }

    return { value: false };
  }
}
